import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { TYPES } from '@/engine/ioc/types';
import { useInjection } from 'inversify-react';
import { useEffect, useState } from 'react';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { ValueConstructor } from '@/engine/DataFlow/DataFlow';

export const useAlgorithmValue = <T extends IValue>(
  descriptor: string,
  constructor: ValueConstructor<T>
): T | undefined => {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);

  const initialValue = planningAlgorithm.has(descriptor)
    ? planningAlgorithm.getValue(descriptor, constructor)
    : undefined;

  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    let subscription: IDisposable | undefined;

    if (planningAlgorithm.has(descriptor)) {
      subscription = planningAlgorithm.addOnChangedCallbackFor<T>(descriptor, setValue);
      const currentValue = planningAlgorithm.getValue(descriptor, constructor);
      setValue(currentValue);
    }

    return (): void => {
      subscription?.dispose();
      subscription = undefined;
    };
  }, [planningAlgorithm, descriptor, constructor]);

  return value;
};
